/**
 * CarrierDriverDetailPage — /carrier/drivers/:driverId
 * Full driver profile edit: CDL, compliance, equipment assignment, sensitive data
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'
import { useState, useEffect } from 'react'
import api, { carrierApi, type FullDriverProfile } from '../lib/api'
import { normalizeZipInput, useZipLocation } from '../lib/useZipLocation'
import { useAuth } from '../lib/auth-context'
import { SupportReadOnlyNotice, useSupportReadOnlyView } from '../lib/support-session'

const CDL_CLASSES   = ['A', 'B', 'C']
const ENDORSEMENTS  = ['H', 'N', 'P', 'T', 'X', 'S', 'L']
const RESTRICTIONS  = ['B', 'E', 'K', 'L', 'M', 'N', 'O', 'V', 'W']
const STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY']

const inputCls = 'w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-500'
const labelCls = 'block text-xs font-medium text-gray-600 mb-1'

function Tags({ label, options, value, onChange, disabled }: {
  label: string; options: string[]; value: string[]; onChange: (v: string[]) => void; disabled?: boolean
}) {
  return (
    <div>
      <div className={labelCls}>{label}</div>
      <div className="flex gap-1 flex-wrap">
        {options.map(tag => (
          <button key={tag} type="button" disabled={disabled}
            onClick={() => onChange(value.includes(tag) ? value.filter(t => t !== tag) : [...value, tag])}
            className={`px-2.5 py-1 rounded text-xs font-semibold border transition-colors
              ${value.includes(tag) ? 'bg-blue-700 text-white border-blue-700' : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400'}
              ${disabled ? 'opacity-60 cursor-default' : ''}`}>
            {tag}
          </button>
        ))}
      </div>
    </div>
  )
}

export default function CarrierDriverDetailPage() {
  const { driverId } = useParams<{ driverId: string }>()
  const { hasRole } = useAuth()
  const { isSupportReadOnlyView } = useSupportReadOnlyView()
  const qc = useQueryClient()
  const canEdit = hasRole('ADMIN', 'AUX_ADMIN', 'SUPER_ADMIN', 'CARRIER_ADMIN') && !isSupportReadOnlyView
  const canViewSensitive = hasRole('ADMIN', 'SUPER_ADMIN', 'AUX_ADMIN')

  const driverQ = useQuery({
    queryKey: ['carrier-driver', driverId],
    queryFn: () => carrierApi.getDriver(driverId!).then(r => r.data),
    enabled: !!driverId,
  })
  const equipQ = useQuery({
    queryKey: ['carrier-equipment'],
    queryFn: () => carrierApi.listEquipment({ status: 'ACTIVE' }).then(r => r.data.units),
  })

  const profile = driverQ.data?.profile

  // ── Form state ────────────────────────────────────────────────────────────────
  const [form, setForm] = useState<Partial<FullDriverProfile>>({})
  const [tractorUnitId, setTractorUnitId] = useState('')
  const [trailerUnitId, setTrailerUnitId] = useState('')
  const [equipMsg, setEquipMsg] = useState<{ ok: boolean; text: string } | null>(null)
  const [profileMsg, setProfileMsg] = useState<{ ok: boolean; text: string } | null>(null)
  const [ssnLast4, setSsnLast4] = useState('')
  const [sensMsg, setSensMsg] = useState<{ ok: boolean; text: string } | null>(null)
  const zipLookup = useZipLocation({
    zip: String(form.zip ?? ''),
    onCity: (city) => setForm(prev => ({ ...prev, city })),
    onState: (state) => setForm(prev => ({ ...prev, state })),
    lookupZip: api.geo.zipLookup,
    enabled: canEdit,
  })

  useEffect(() => {
    if (!profile) return
    setForm({
      firstName:            profile.firstName ?? '',
      middleName:           profile.middleName ?? '',
      lastName:             profile.lastName ?? '',
      legalName:            profile.legalName ?? '',
      dateOfBirth:          profile.dateOfBirth ?? '',
      email:                profile.email ?? '',
      phone:                profile.phone ?? '',
      addressLine1:         profile.addressLine1 ?? '',
      addressLine2:         profile.addressLine2 ?? '',
      city:                 profile.city ?? '',
      state:                profile.state ?? '',
      zip:                  profile.zip ?? '',
      emergencyContactName: profile.emergencyContactName ?? '',
      emergencyContactPhone:profile.emergencyContactPhone ?? '',
      cdlClass:             profile.cdlClass ?? '',
      licenseNumber:        profile.licenseNumber ?? '',
      licenseState:         profile.licenseState ?? '',
      licenseExpires:       profile.licenseExpires ?? '',
      medicalCertExpires:   profile.medicalCertExpires ?? '',
      endorsements:         profile.endorsements ?? [],
      restrictions:         profile.restrictions ?? [],
      yearsExperience:      profile.yearsExperience ?? undefined,
      mvrLastChecked:       profile.mvrLastChecked ?? '',
      mvrStatus:            profile.mvrStatus ?? '',
      backgroundCheckStatus:profile.backgroundCheckStatus ?? '',
      drugTestStatus:       profile.drugTestStatus ?? '',
      hireDate:             profile.hireDate ?? '',
      driverStatus:         profile.driverStatus ?? 'ACTIVE',
      unitLabel:            profile.unitLabel ?? '',
      notes:                profile.notes ?? '',
      maxVehicleCapacity:   profile.maxVehicleCapacity ?? undefined,
      operatingStates:      profile.operatingStates ?? [],
      preferredLanes:       profile.preferredLanes ?? [],
    })
    setTractorUnitId(profile.defaultTractorUnitId ?? '')
    setTrailerUnitId(profile.defaultTrailerUnitId ?? '')
  }, [profile])

  const f = (k: keyof FullDriverProfile) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setForm(prev => ({ ...prev, [k]: e.target.value }))

  const normalizeProfilePayload = (raw: Partial<FullDriverProfile>) => {
    const payload: Record<string, any> = { ...raw }
    const dateKeys: Array<keyof FullDriverProfile> = [
      'dateOfBirth', 'licenseExpires', 'medicalCertExpires',
      'mvrLastChecked', 'drugTestDate', 'hireDate',
    ]
    for (const key of dateKeys) {
      if (payload[key] === '') payload[key] = null
    }
    const numberKeys: Array<keyof FullDriverProfile> = ['yearsExperience', 'maxVehicleCapacity']
    for (const key of numberKeys) {
      if (payload[key] === '') payload[key] = null
    }
    return payload
  }

  const profileMut = useMutation({
    mutationFn: () => carrierApi.updateDriver(driverId!, normalizeProfilePayload(form)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['carrier-driver', driverId] })
      qc.invalidateQueries({ queryKey: ['carrier-drivers'] })
      setProfileMsg({ ok: true, text: 'Profile saved.' })
      setTimeout(() => setProfileMsg(null), 3000)
    },
    onError: (err: any) => setProfileMsg({ ok: false, text: err?.response?.data?.error ?? err.message }),
  })

  const equipMut = useMutation({
    mutationFn: () => carrierApi.assignEquipment(driverId!, {
      tractorUnitId: tractorUnitId || undefined,
      trailerUnitId: trailerUnitId || undefined,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['carrier-driver', driverId] })
      qc.invalidateQueries({ queryKey: ['carrier-drivers'] })
      setEquipMsg({ ok: true, text: 'Equipment assigned. Trailer planner will auto-load this combo.' })
      setTimeout(() => setEquipMsg(null), 5000)
    },
    onError: (err: any) => setEquipMsg({ ok: false, text: err?.response?.data?.error ?? err.message }),
  })

  const sensMut = useMutation({
    mutationFn: () => carrierApi.updateSensitive(driverId!, { ssnLast4: ssnLast4 || undefined }),
    onSuccess: () => {
      setSsnLast4('')
      setSensMsg({ ok: true, text: 'Sensitive data updated.' })
      setTimeout(() => setSensMsg(null), 3000)
    },
    onError: (err: any) => setSensMsg({ ok: false, text: err?.response?.data?.error ?? err.message }),
  })

  if (driverQ.isLoading) return <div className="p-6 text-gray-400 text-sm">Loading driver…</div>
  if (!profile)          return <div className="p-6 text-red-600 text-sm">Driver profile not found.</div>

  const tractors = (equipQ.data ?? []).filter(u => u.unitType === 'tractor')
  const trailers = (equipQ.data ?? []).filter(u => u.unitType === 'trailer')
  const warnings = profile.complianceWarnings ?? []
  const errorWarnings = warnings.filter(w => w.includes('_EXPIRED') || w.includes('FAILED'))

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-8">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {form.firstName || form.lastName
              ? `${form.firstName ?? ''} ${form.lastName ?? ''}`.trim()
              : driverQ.data?.name ?? 'Driver Profile'}
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">{driverQ.data?.email}</p>
          {errorWarnings.length > 0 && (
            <div className="mt-2 space-y-1">
              {errorWarnings.map((w, i) => (
                <div key={i} className="text-xs font-medium text-red-700">⚠ {w}</div>
              ))}
            </div>
          )}
        </div>
        <Link to="/carrier/drivers" className="text-sm text-blue-600 hover:text-blue-800">← Roster</Link>
      </div>

      {isSupportReadOnlyView ? (
        <SupportReadOnlyNotice />
      ) : !canEdit ? (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-4 py-2 text-sm">
          View only — you do not have permission to edit driver profiles.
        </div>
      ) : null}

      {/* Compliance Summary */}
      {warnings.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <div className="text-sm font-semibold text-amber-900 mb-2">Compliance Warnings</div>
          <div className="space-y-1">
            {warnings.map((w, i) => (
              <div key={i} className={`text-xs ${w.includes('_EXPIRED') || w.includes('FAILED') ? 'text-red-700 font-medium' : 'text-amber-800'}`}>
                {w.includes('EXPIRED') || w.includes('FAILED') ? '✗' : '⚠'} {w}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Identity + Contact */}
      <section className="bg-white border border-gray-200 rounded-2xl shadow-sm p-6">
        <h2 className="text-base font-semibold text-gray-900 mb-4">Identity &amp; Contact</h2>
        {profileMsg && (
          <div className={`mb-4 rounded-lg px-4 py-3 text-sm border ${profileMsg.ok ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-700'}`}>
            {profileMsg.text}
          </div>
        )}
        <div className="grid grid-cols-2 gap-4">
          {([['firstName','First Name','First'],['middleName','Middle','Mid'],['lastName','Last Name','Last']] as const).map(([k,label,ph]) => (
            <div key={k}>
              <label className={labelCls}>{label}</label>
              <input value={(form[k as keyof typeof form] as string) ?? ''} onChange={f(k as any)}
                placeholder={ph} disabled={!canEdit} className={inputCls} />
            </div>
          ))}
          <div>
            <label className={labelCls}>Date of Birth</label>
            <input type="date" value={(form.dateOfBirth as string) ?? ''} onChange={f('dateOfBirth')} disabled={!canEdit} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Phone</label>
            <input value={(form.phone as string) ?? ''} onChange={f('phone')} placeholder="+1 555-0100" disabled={!canEdit} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Email</label>
            <input value={(form.email as string) ?? ''} onChange={f('email')} disabled={!canEdit} className={inputCls} />
          </div>
          <div className="col-span-2">
            <label className={labelCls}>Address</label>
            <input value={(form.addressLine1 as string) ?? ''} onChange={f('addressLine1')} placeholder="Street address" disabled={!canEdit} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>City</label>
            <input value={(form.city as string) ?? ''} onChange={f('city')} disabled={!canEdit} className={inputCls} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className={labelCls}>State</label>
              <select value={(form.state as string) ?? ''} onChange={f('state')} disabled={!canEdit} className={inputCls}>
                <option value="">—</option>
                {STATES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>ZIP</label>
              <input value={(form.zip as string) ?? ''} onChange={(e) => setForm(prev => ({ ...prev, zip: normalizeZipInput(e.target.value) }))} disabled={!canEdit} maxLength={10} inputMode="numeric" className={inputCls} />
              {(zipLookup.loading || zipLookup.message) && <p className="mt-1 text-xs text-gray-500" role="status">{zipLookup.loading ? 'Looking up ZIP...' : zipLookup.message}</p>}
            </div>
          </div>
          <div>
            <label className={labelCls}>Emergency Contact</label>
            <input value={(form.emergencyContactName as string) ?? ''} onChange={f('emergencyContactName')} disabled={!canEdit} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Emergency Phone</label>
            <input value={(form.emergencyContactPhone as string) ?? ''} onChange={f('emergencyContactPhone')} disabled={!canEdit} className={inputCls} />
          </div>
        </div>
      </section>

      {/* CDL + Compliance */}
      <section className="bg-white border border-gray-200 rounded-2xl shadow-sm p-6">
        <h2 className="text-base font-semibold text-gray-900 mb-4">CDL &amp; Compliance</h2>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelCls}>CDL Class</label>
            <select value={(form.cdlClass as string) ?? ''} onChange={f('cdlClass')} disabled={!canEdit} className={inputCls}>
              <option value="">Not set</option>
              {CDL_CLASSES.map(c => <option key={c} value={c}>CDL-{c}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>License State</label>
            <select value={(form.licenseState as string) ?? ''} onChange={f('licenseState')} disabled={!canEdit} className={inputCls}>
              <option value="">—</option>
              {STATES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>License Number</label>
            <input value={(form.licenseNumber as string) ?? ''} onChange={f('licenseNumber')} disabled={!canEdit} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>License Expires</label>
            <input type="date" value={(form.licenseExpires as string) ?? ''} onChange={f('licenseExpires')} disabled={!canEdit} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Medical Card Expires</label>
            <input type="date" value={(form.medicalCertExpires as string) ?? ''} onChange={f('medicalCertExpires')} disabled={!canEdit} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>MVR Last Checked</label>
            <input type="date" value={(form.mvrLastChecked as string) ?? ''} onChange={f('mvrLastChecked')} disabled={!canEdit} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>MVR Status</label>
            <select value={(form.mvrStatus as string) ?? ''} onChange={f('mvrStatus')} disabled={!canEdit} className={inputCls}>
              <option value="">Not set</option>
              <option value="CLEAR">Clear</option>
              <option value="REVIEW">Under Review</option>
              <option value="FAILED">Failed</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>Background Check</label>
            <select value={(form.backgroundCheckStatus as string) ?? ''} onChange={f('backgroundCheckStatus')} disabled={!canEdit} className={inputCls}>
              <option value="">Not set</option>
              <option value="PENDING">Pending</option>
              <option value="CLEAR">Clear</option>
              <option value="REVIEW">Under Review</option>
              <option value="FAILED">Failed</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>Drug Test Status</label>
            <select value={(form.drugTestStatus as string) ?? ''} onChange={f('drugTestStatus')} disabled={!canEdit} className={inputCls}>
              <option value="">Not set</option>
              <option value="PENDING">Pending</option>
              <option value="CLEAR">Clear</option>
              <option value="FAILED">Failed</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>Hire Date</label>
            <input type="date" value={(form.hireDate as string) ?? ''} onChange={f('hireDate')} disabled={!canEdit} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Driver Status</label>
            <select value={(form.driverStatus as string) ?? 'ACTIVE'} onChange={f('driverStatus')} disabled={!canEdit} className={inputCls}>
              <option value="ACTIVE">Active</option>
              <option value="INACTIVE">Inactive</option>
              <option value="SUSPENDED">Suspended</option>
              <option value="TERMINATED">Terminated</option>
            </select>
          </div>
        </div>
        <div className="mt-4 space-y-3">
          <Tags label="Endorsements" options={ENDORSEMENTS}
            value={(form.endorsements as string[]) ?? []}
            onChange={vals => setForm(p => ({ ...p, endorsements: vals }))}
            disabled={!canEdit} />
          <Tags label="Restrictions" options={RESTRICTIONS}
            value={(form.restrictions as string[]) ?? []}
            onChange={vals => setForm(p => ({ ...p, restrictions: vals }))}
            disabled={!canEdit} />
        </div>
        <div className="grid grid-cols-2 gap-4 mt-4">
          <div>
            <label className={labelCls}>Unit Label (internal)</label>
            <input value={(form.unitLabel as string) ?? ''} onChange={f('unitLabel')} placeholder="Unit 12" disabled={!canEdit} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Max Vehicle Capacity</label>
            <input type="number" value={(form.maxVehicleCapacity as number) ?? ''} onChange={f('maxVehicleCapacity')} min={0} max={20} disabled={!canEdit} className={inputCls} />
          </div>
          <div className="col-span-2">
            <label className={labelCls}>Notes (dispatcher-only)</label>
            <textarea value={(form.notes as string) ?? ''} onChange={f('notes')} rows={2} disabled={!canEdit}
              className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none disabled:bg-gray-50" />
          </div>
        </div>
        {canEdit && (
          <button onClick={() => profileMut.mutate()} disabled={profileMut.isPending}
            className="mt-4 bg-blue-700 text-white text-sm font-semibold px-5 py-2.5 rounded-lg hover:bg-blue-800 disabled:opacity-50">
            {profileMut.isPending ? 'Saving…' : 'Save Profile'}
          </button>
        )}
      </section>

      {/* Equipment Assignment */}
      <section className="bg-white border border-gray-200 rounded-2xl shadow-sm p-6">
        <h2 className="text-base font-semibold text-gray-900 mb-1">Equipment Assignment</h2>
        <p className="text-sm text-gray-500 mb-4">Assigned units auto-populate the trailer planner when this driver opens a job.</p>
        {equipMsg && (
          <div className={`mb-4 rounded-lg px-4 py-3 text-sm border ${equipMsg.ok ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-700'}`}>
            {equipMsg.text}
          </div>
        )}
        {driverQ.data?.tractorUnit && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm">
            <span className="font-semibold text-blue-900">Current: </span>
            {driverQ.data.tractorUnit.displayName ?? driverQ.data.tractorUnit.unitNumber}
            {driverQ.data.trailerUnit && ` + ${driverQ.data.trailerUnit.displayName ?? driverQ.data.trailerUnit.unitNumber}`}
          </div>
        )}
        {canEdit && (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Tractor Unit</label>
              <select value={tractorUnitId} onChange={e => setTractorUnitId(e.target.value)} className={inputCls}>
                <option value="">— None —</option>
                {tractors.map(u => (
                  <option key={u.id} value={u.id}>
                    {u.unitNumber ? `${u.unitNumber} — ` : ''}{u.displayName ?? (`${u.make ?? ''} ${u.model ?? ''}`.trim() || u.id.slice(0,8))}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Trailer Unit</label>
              <select value={trailerUnitId} onChange={e => setTrailerUnitId(e.target.value)} className={inputCls}>
                <option value="">— None —</option>
                {trailers.map(u => (
                  <option key={u.id} value={u.id}>
                    {u.unitNumber ? `${u.unitNumber} — ` : ''}{u.displayName ?? (`${u.make ?? ''} ${u.model ?? ''}`.trim() || u.id.slice(0,8))}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-span-2">
              <button onClick={() => equipMut.mutate()} disabled={equipMut.isPending || (!tractorUnitId && !trailerUnitId)}
                className="bg-blue-700 text-white text-sm font-semibold px-5 py-2.5 rounded-lg hover:bg-blue-800 disabled:opacity-50">
                {equipMut.isPending ? 'Assigning…' : 'Assign Equipment'}
              </button>
            </div>
          </div>
        )}
      </section>

      {/* Sensitive Data (admin only) */}
        {canViewSensitive && (
          <section className="bg-white border border-amber-200 rounded-2xl shadow-sm p-6">
            <h2 className="text-base font-semibold text-gray-900 mb-1">Sensitive Data</h2>
            <p className="text-xs text-amber-700 mb-4">
            Restricted access. This view is logged to the audit trail. Full SSN is never returned.
          </p>
          {sensMsg && (
            <div className={`mb-4 rounded-lg px-4 py-3 text-sm border ${sensMsg.ok ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-700'}`}>
              {sensMsg.text}
            </div>
          )}
          {canEdit && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>SSN Last 4 Digits Only</label>
                <input value={ssnLast4} onChange={e => setSsnLast4(e.target.value.replace(/\D/g, '').slice(0,4))}
                  placeholder="4 digits" maxLength={4} className={inputCls} />
                <p className="text-xs text-gray-400 mt-1">Only last 4 digits. Never enter or store full SSN here.</p>
              </div>
              <div className="flex items-end">
                <button onClick={() => sensMut.mutate()} disabled={sensMut.isPending || ssnLast4.length !== 4}
                  className="bg-amber-700 text-white text-sm font-semibold px-4 py-2.5 rounded-lg hover:bg-amber-800 disabled:opacity-50">
                  {sensMut.isPending ? 'Saving…' : 'Update SSN Last 4'}
                </button>
              </div>
            </div>
          )}
        </section>
      )}
    </div>
  )
}
